import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, LogOut, Settings, Smartphone, UserRound } from "lucide-react";
import { Avatar } from "@/components/ui/avatar";
import { useAuthStore } from "@/stores/auth-store";
import { usePermission } from "@/hooks/use-permission";
import { cn } from "@/lib/utils";

const MENU_ITEMS = [
  { id: "account", label: "My Account", icon: UserRound, href: "/account" },
  { id: "settings", label: "Settings", icon: Settings, href: "/settings", permission: "settings:read" as const },
  // ALL-5141: every signed-in human can get the signer app; no permission gate.
  { id: "get-app", label: "Get the mobile app", icon: Smartphone, href: "/get-app/android" },
];

export default function ProfileDropdown() {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const signOut = useAuthStore((s) => s.signOut);
  const user = useAuthStore((s) => s.user);
  const canAccessSettings = usePermission("settings:read");

  const displayUser = user ?? { name: "User", email: "", initials: "U" };

  const close = useCallback(() => setOpen(false), []);

  const items = useMemo(
    () => [
      ...MENU_ITEMS
        .filter((i) => !i.permission || (i.permission === "settings:read" && canAccessSettings))
        .map((i) => ({ ...i, action: () => i.href && navigate(i.href) })),
      { id: "signout", label: "Sign Out", icon: LogOut, action: () => { signOut().then(() => navigate("/login")); } },
    ],
    [navigate, signOut, canAccessSettings]
  );

  useEffect(() => {
    function onPointerDown(e: MouseEvent | TouchEvent) {
      if (!rootRef.current?.contains(e.target as Node)) close();
    }
    function onKeyDown(e: KeyboardEvent) {
      if (!open) return;
      if (e.key === "Escape") {
        close();
        triggerRef.current?.focus();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % items.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => (i - 1 + items.length) % items.length);
      } else if (e.key === "Enter" || e.key === " ") {
        // Enter/Space on the trigger belong to the trigger — they toggle the
        // menu shut (see the trigger's own onKeyDown). This listener is on
        // `document`, so without this guard the same keypress also activated
        // the highlighted item, and Enter on the trigger closed the menu and
        // navigated to My Account (or signed the person out, if the highlight
        // had been moved to Sign Out first).
        if (triggerRef.current?.contains(e.target as Node)) return;
        if (activeIndex >= 0) items[activeIndex].action();
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, activeIndex, items, close]);

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`User menu · ${displayUser.name}`}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        className="flex items-center gap-2.5 rounded-md px-2 py-1 outline-none focus-visible:ring-2 focus-visible:ring-primary-soft hover:bg-bg"
      >
        <Avatar initials={displayUser.initials} size={32} bgColor="var(--ads-primary)" />
        <span className="hidden max-w-[190px] text-left leading-tight xl:block">
          <span className="block truncate text-base font-semibold leading-snug text-ink">{displayUser.name}</span>
          <span className="block max-w-[170px] truncate text-sm leading-snug text-muted">{displayUser.email}</span>
        </span>
        <ChevronDown aria-hidden className={cn("size-4 text-muted transition-transform", open && "rotate-180")} />
      </button>

      {open && (
        <div
          role="menu"
          aria-label="User menu"
          className="absolute right-0 z-50 mt-2 w-72 overflow-hidden rounded-lg border border-line bg-surface shadow"
        >
          <div className="border-b border-line px-4 py-3.5">
            <div className="flex items-center gap-3">
              <Avatar initials={displayUser.initials} size={40} bgColor="var(--ads-primary)" />
              <div className="min-w-0 leading-tight">
                <div className="truncate text-sm font-semibold text-ink">{displayUser.name}</div>
                <div className="truncate text-xs text-muted">{displayUser.email}</div>
              </div>
            </div>
          </div>
          <div className="p-1.5">
            {items.map((item, idx) => (
              <Fragment key={item.id}>
                {item.id === "settings" && (
                  <div role="presentation" className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wider text-muted">
                    Settings
                  </div>
                )}
              <button
                type="button"
                role="menuitem"
                onMouseEnter={() => setActiveIndex(idx)}
                onClick={item.action}
                data-highlighted={idx === activeIndex ? "true" : undefined}
                className={cn(
                  "flex w-full items-center gap-2.5 rounded-sm px-3 py-2 text-left text-sm font-medium text-text outline-none focus-visible:ring-2 focus-visible:ring-primary-soft",
                  // Sign Out keeps its danger treatment when it is the highlighted
                  // item; a shared `text-ink` highlight would recolour it like an
                  // ordinary action and drop the affordance mid-keyboard-walk.
                  item.id === "signout"
                    ? idx === activeIndex && "bg-danger-soft text-danger"
                    : idx === activeIndex && "bg-primary-soft text-ink",
                  item.id === "signout" && "text-danger"
                )}
              >
                <item.icon aria-hidden className="size-4 shrink-0" />
                {item.label}
              </button>
              </Fragment>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
